import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Sprout, MessageCircleHeart, HandHeart, Quote, Phone } from "lucide-react";
import useReveal from "../hooks/useReveal";
import usePageMeta from "../hooks/usePageMeta";
import { SERVICES, PARTNERS, SITE } from "../data/site";

const iconMap = { Users, Sprout, MessageCircleHeart, HandHeart };

export default function Home() {
  useReveal();
  usePageMeta({
    title: "Home",
    description:
      "Thrive Bridge Mental Wellness CIC — mental health and wellbeing support for adults in the UK facing social and economic disadvantage.",
  });

  return (
    <>
      {/* HERO */}
      <section className="bg-[var(--tb-surface)] border-b border-[var(--tb-border)]">
        <div className="container-page pt-14 pb-16 md:pt-20 md:pb-24 lg:pt-24 lg:pb-28 grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--tb-text)] text-balance">
              Mental health and wellbeing support for adults across the UK
            </h1>
            <p className="mt-6 text-lg md:text-xl text-[var(--tb-text-2)] max-w-2xl text-pretty">
              Thrive Bridge is a UK Community Interest Company. We provide trauma-informed, culturally responsive
              support to adults affected by migration, violence, homelessness, poverty and social exclusion.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/get-support"
                data-testid="hero-get-support-button"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--tb-primary)] hover:bg-[var(--tb-primary-hover)] text-white px-6 py-3.5 font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
              >
                Get Support <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/get-support#professional"
                data-testid="hero-refer-someone-button"
                className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--tb-primary)] text-[var(--tb-primary)] hover:bg-white px-6 py-3.5 font-semibold transition-colors"
              >
                Refer Someone
              </Link>
              <a
                href={`tel:${SITE.phoneRaw}`}
                data-testid="hero-call-button"
                className="inline-flex items-center gap-2 rounded-full text-[var(--tb-primary)] hover:bg-white/60 px-4 py-3.5 font-semibold transition-colors"
              >
                <Phone className="h-4 w-4" /> {SITE.phone}
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-[var(--tb-border)] shadow-sm">
              <img
                src="https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&w=900"
                alt="Group of people standing together with hands joined"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* HOW TO GET SUPPORT — quick-scan for service users */}
      <section className="container-page section-spacing">
        <div className="max-w-3xl">
          <h2 className="font-serif-display text-3xl md:text-4xl text-[var(--tb-text)]">How to get support</h2>
          <p className="mt-3 text-[var(--tb-text-2)] text-lg text-pretty">
            There are three ways to reach us. All are free and confidential.
          </p>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-3">
          <article className="bg-white border border-[var(--tb-border)] rounded-2xl p-6">
            <p className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Option 1</p>
            <h3 className="mt-2 font-serif-display text-xl text-[var(--tb-text)]">Refer yourself</h3>
            <p className="mt-2 text-[var(--tb-text-2)]">
              Fill in our short self-referral form. It takes about 3 minutes.
            </p>
            <Link
              to="/get-support#self"
              data-testid="home-self-refer-link"
              className="mt-4 inline-flex items-center gap-1.5 text-[var(--tb-primary)] font-semibold hover:underline"
            >
              Click here to self-refer <ArrowRight className="h-4 w-4" />
            </Link>
          </article>

          <article className="bg-white border border-[var(--tb-border)] rounded-2xl p-6">
            <p className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Option 2</p>
            <h3 className="mt-2 font-serif-display text-xl text-[var(--tb-text)]">Ask a professional to refer you</h3>
            <p className="mt-2 text-[var(--tb-text-2)]">
              Your GP, housing officer, support worker or key worker can refer you with your consent.
            </p>
            <Link
              to="/get-support#professional"
              data-testid="home-professional-refer-link"
              className="mt-4 inline-flex items-center gap-1.5 text-[var(--tb-primary)] font-semibold hover:underline"
            >
              Click here to refer someone <ArrowRight className="h-4 w-4" />
            </Link>
          </article>

          <article className="bg-white border border-[var(--tb-border)] rounded-2xl p-6">
            <p className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Option 3</p>
            <h3 className="mt-2 font-serif-display text-xl text-[var(--tb-text)]">Call or email us</h3>
            <p className="mt-2 text-[var(--tb-text-2)]">
              Phone <a href={`tel:${SITE.phoneRaw}`} className="text-[var(--tb-primary)] font-semibold">{SITE.phone}</a>{" "}
              or email <a href={`mailto:${SITE.email}`} className="text-[var(--tb-primary)] font-semibold">{SITE.email}</a>.
            </p>
            <p className="mt-4 text-sm text-[var(--tb-muted)]">Office hours: {SITE.hours}</p>
          </article>
        </div>

        {/* Waiting list notice */}
        <div
          role="note"
          data-testid="waiting-list-notice"
          className="mt-8 rounded-2xl border border-[var(--tb-border)] bg-[var(--tb-surface)] p-5 md:p-6 text-[var(--tb-text-2)]"
        >
          <p className="text-sm md:text-base">
            <span className="font-semibold text-[var(--tb-text)]">Please note:</span> we currently operate a
            waiting list. After you refer, we will contact you within 5 working days to explain expected wait
            times and next steps. If you are in crisis, please call{" "}
            <a href="tel:999" className="text-[var(--tb-crisis)] underline font-semibold">999</a> or Samaritans on{" "}
            <a href="tel:116123" className="text-[var(--tb-crisis)] underline font-semibold">116 123</a>.
          </p>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing grid gap-10 lg:grid-cols-12 items-start">
          <div className="lg:col-span-5 fade-up">
            <span className="text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)]">Who we are</span>
            <h2 className="font-serif-display mt-3 text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)] text-balance">
              A UK Community Interest Company
            </h2>
          </div>
          <div className="lg:col-span-7 fade-up">
            <p className="text-lg text-[var(--tb-text-2)] text-pretty">
              We work with adults whose mental health has been affected by adversity — migration, abuse,
              homelessness, poverty or exclusion — and with the people and organisations supporting them.
            </p>
            <p className="mt-4 text-lg text-[var(--tb-text-2)] text-pretty">
              Our approach is trauma-informed, culturally responsive and community-led. As a Community Interest
              Company, our profits and assets are legally locked to our mission.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="container-page section-spacing">
        <div className="max-w-3xl">
          <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-[var(--tb-text)] text-balance">
            What we offer
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:gap-8 md:grid-cols-3">
          {SERVICES.slice(0, 3).map((s, i) => {
            const Icon = iconMap[s.icon] || Users;
            return (
              <article
                key={s.slug}
                className="fade-up bg-white border border-[var(--tb-border)] rounded-2xl p-7 md:p-8 hover:-translate-y-1 hover:shadow-lg transition-all"
                style={{ transitionDelay: `${i * 60}ms` }}
                data-testid={`feature-${s.slug}`}
              >
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[var(--tb-surface)] text-[var(--tb-primary)] mb-5">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="font-serif-display text-2xl text-[var(--tb-text)] mb-2">{s.title}</h3>
                <p className="text-[var(--tb-text-2)]">{s.description}</p>
              </article>
            );
          })}
        </div>
        <div className="mt-10">
          <Link
            to="/services"
            data-testid="home-services-link"
            className="inline-flex items-center gap-2 text-[var(--tb-primary)] font-semibold hover:gap-3 transition-all"
          >
            See all our services <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="bg-[var(--tb-surface)] border-y border-[var(--tb-border)]">
        <div className="container-page section-spacing grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-5 fade-up">
            <div className="relative aspect-square rounded-3xl overflow-hidden border border-[var(--tb-border)]">
              <img
                src="https://images.unsplash.com/photo-1643012177761-cc4949dfa8ba?crop=entropy&cs=srgb&fm=jpg&w=900"
                alt="A person holding a warm cup of tea"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 fade-up">
            <Quote className="h-9 w-9 text-[var(--tb-primary)] mb-4" aria-hidden="true" />
            <blockquote className="font-serif-display text-2xl md:text-3xl leading-snug text-[var(--tb-text)] text-balance">
              &ldquo;I didn&rsquo;t have to explain my whole life to be understood. They met me where I was.&rdquo;
            </blockquote>
            <p className="mt-5 text-[var(--tb-text-2)]">— Service user, Wellbeing Workshop</p>
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="container-page py-14 md:py-20">
        <p className="text-center text-xs font-semibold tracking-widest uppercase text-[var(--tb-primary)] mb-8">
          Working with partners &amp; funders
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {PARTNERS.map((p) => (
            <div
              key={p}
              className="rounded-xl bg-white border border-[var(--tb-border)] py-5 px-3 text-center text-sm font-medium text-[var(--tb-text-2)]"
            >
              {p}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-page pb-16 md:pb-24">
        <div className="rounded-3xl bg-[var(--tb-secondary)] text-white p-8 md:p-12 grid gap-8 lg:grid-cols-12 items-center">
          <div className="lg:col-span-8">
            <h2 className="font-serif-display text-2xl md:text-3xl text-balance">
              Ready to take the next step?
            </h2>
            <p className="mt-3 text-white/90 text-lg max-w-2xl text-pretty">
              Whether you&rsquo;re reaching out for yourself or someone you support, we&rsquo;ll respond within 5 working days.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end gap-3 flex-wrap">
            <Link
              to="/get-support#self"
              data-testid="cta-self-referral-button"
              className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tb-secondary)] hover:bg-[var(--tb-surface)] px-5 py-3 font-semibold"
            >
              Self-refer <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/get-support#professional"
              data-testid="cta-professional-referral-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-white/70 hover:border-white text-white px-5 py-3 font-semibold"
            >
              Professional referral
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
